/**
 * PublicContactStrip — the single, authoritative contact block for
 * every PUBLIC landing / province / city / feature / insight page.
 *
 * Central Rentals Canada Inc. — contact email + Canadian phone number.
 * Do NOT hard-code contact info anywhere else on the marketing site —
 * import this component so every page stays in lockstep.
 */
import { Mail, Phone, MapPin } from "lucide-react";

export const CENTRAL_RENTALS = Object.freeze({
  company: "Central Rentals Canada Inc.",
  email: "contact@centralrentals.ca",
  phone: "780-405-2498",
  phone_tel: "+17804052498",
  address_line: "Registered in Alberta, Canada",
});

export default function PublicContactStrip({ variant = "light", testid = "public-contact-strip" }) {
  const dark = variant === "dark";
  return (
    <section
      data-testid={testid}
      style={{
        background: dark
          ? "linear-gradient(135deg,#0b1220 0%,#0f1f37 100%)"
          : "linear-gradient(180deg,#ecfdf5 0%,#ffffff 100%)",
        color: dark ? "#e2e8f0" : "#0f172a",
        borderTop: dark ? "1px solid rgba(148,163,184,0.14)" : "1px solid #d1fae5",
        borderBottom: dark ? "1px solid rgba(148,163,184,0.14)" : "1px solid #d1fae5",
      }}
      className="w-full py-8 sm:py-10"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <div
            className="text-[11px] uppercase tracking-[0.22em] font-bold"
            style={{ color: dark ? "#6ee7b7" : "#059669" }}
          >
            Talk to a Canadian human
          </div>
          <div className="mt-2 font-display font-extrabold text-xl sm:text-2xl tracking-tight">
            {CENTRAL_RENTALS.company}
          </div>
          <div className="mt-1 text-sm" style={{ color: dark ? "#94a3b8" : "#475569" }}>
            Mon–Fri · 9 AM – 6 PM MT · Bilingual (EN / FR)
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <a
            href={`tel:${CENTRAL_RENTALS.phone_tel}`}
            data-testid={`${testid}-phone`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px 18px",
              borderRadius: "9999px",
              background: dark ? "rgba(16,185,129,0.15)" : "#059669",
              color: dark ? "#6ee7b7" : "#ffffff",
              fontWeight: 700,
              textDecoration: "none",
              border: dark ? "1px solid rgba(16,185,129,0.35)" : "0",
            }}
          >
            <Phone className="w-4 h-4" /> {CENTRAL_RENTALS.phone}
          </a>
          <a
            href={`mailto:${CENTRAL_RENTALS.email}`}
            data-testid={`${testid}-email`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px 18px",
              borderRadius: "9999px",
              background: dark ? "rgba(255,255,255,0.06)" : "#ffffff",
              color: dark ? "#e2e8f0" : "#0f172a",
              fontWeight: 700,
              textDecoration: "none",
              border: dark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #cbd5e1",
            }}
          >
            <Mail className="w-4 h-4" /> {CENTRAL_RENTALS.email}
          </a>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-5 text-xs" style={{ color: dark ? "#64748b" : "#64748b" }}>
        <span className="inline-flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" /> {CENTRAL_RENTALS.address_line}
        </span>
      </div>
    </section>
  );
}
